import loadable from "@loadable/component";
import { Button } from "@tvg/fuse/button";
import { ContentPattern as FuseContentPattern } from "@tvg/fuse/content-pattern";
import { GridContainer, GridItem, GridRow } from "@tvg/fuse/grid";

import * as Icons from "@tvg/fuse/icon/all";
import { Link } from "@tvg/fuse/link";
import { SpacingWrapper } from "@tvg/fuse/spacing-wrapper";
import { ImageVideoAlignment, ImageVideoPosition } from "amp-types/tabs-schema";
import useGlobalConfig from "client/context/GlobalConfig";
import getEventHandler from "client/utils/eventHandler";
import getIconName from "client/utils/getIconName";
import getImageSources from "client/utils/getImageSources";
import React, { useMemo } from "react";
import { AmpContentPattern, AmpContentPatternCTA, AmpContentPatternLink } from "types/amplience";
import { getCTAEvents } from "../../hooks/useCTAEvents";
import { iconColourConfig } from "./config";

export interface ContentPatternProps {
  config: AmpContentPattern;
}

const VideoPlayer = loadable(() => import(/* webpackChunkName: "VideoPlayer" */ "../VideoPlayer/VideoPlayer"));
const TextBlock = loadable(() => import(/* webpackChunkName: "TextBlock" */ "client/components/TextBlock/TextBlock"), {
  resolveComponent: (m) => m.TextBlock,
});

const ContentPattern: React.FC<ContentPatternProps> = ({
  config: {
    brandLogo,
    content,
    icon,
    iconColour = "Black",
    image,
    imageRatio,
    imageAlignment,
    imagePosition,
    video,
    _meta,
  },
  ...props
}) => {
  const { brand } = useGlobalConfig();

  const buttons = content?.ctaContentPattern?.content?.cta as AmpContentPatternCTA[];
  const hasButton = !!buttons && buttons?.length > 0;
  const links = content?.ctaContentPattern?.content?.link as AmpContentPatternLink[];
  const hasLink = !!links && links?.length > 0;

  const Icon = icon && Icons[getIconName(icon) as keyof typeof Icons];

  const configProps = useMemo(() => {
    return {
      brandLogo: brandLogo && {
        alt: `${brandLogo} logo`,
        brand: brandLogo,
      },
      icon: Icon && <Icon fill={iconColourConfig[iconColour]} />,
      imageWrapper: {
        imageAlignment: imageAlignment?.toLowerCase() as Lowercase<ImageVideoAlignment>,
        imagePosition: imagePosition?.toLowerCase() as Lowercase<ImageVideoPosition>,
        imageRatio,
      },
      picture:
        image &&
        ({
          ...getImageSources(image, {
            brand,
            w: 740,
            qlt: "default",
            templates: ["poi"],
          }),
          alt: "",
          isLazy: true,
          fetchPriority: "auto",
        } as const),

      videoPlayerSlot: video && <VideoPlayer hideHeadlines ratio={imageRatio} gridWrapper={false} config={video} />,
    };
  }, [brandLogo, Icon, iconColour, imageAlignment, imagePosition, imageRatio, image, brand, video]);

  const mainContentText = content?.mainContent?.mainContentText;

  const textBlockConfig = useMemo(
    () => ({
      _meta,
      text: mainContentText || "",
    }),
    [_meta, mainContentText],
  );

  return (
    <SpacingWrapper marginBottom="l">
      <GridContainer>
        <GridRow>
          <GridItem xxs={12} l={10} colStart-l={2}>
            <FuseContentPattern dataTestId="content-content-pattern" {...props} {...configProps}>
              {mainContentText && <TextBlock config={textBlockConfig} gridWrapper={false} noSidePadding />}
              {hasButton &&
                buttons.map((button: AmpContentPatternCTA, i: number) => (
                  <Button
                    key={i}
                    data-cta-event={getCTAEvents(button)}
                    onClick={getEventHandler(button.publishEvent)}
                    href={button.url}
                    size={{ xxs: "small", m: "medium", l: "large" }}
                    slot="cta"
                    type="button"
                    variant={i === 0 ? "secondary" : "tertiary"}
                  >
                    {button.text}
                  </Button>
                ))}
              {hasLink &&
                links.map((link: AmpContentPatternLink, i: number) => (
                  <Link key={i} href={link.url} icon={<Icons.IconChevronRight />} iconRight slot="cta">
                    {link.text}
                  </Link>
                ))}
            </FuseContentPattern>
          </GridItem>
        </GridRow>
      </GridContainer>
    </SpacingWrapper>
  );
};

export default React.memo(ContentPattern);
